import React, { useCallback, useId } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import Select, {
    MenuPlacement, MenuPosition, Options, SingleValue, components, OptionProps,
} from 'react-select';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';

import { selectBaseStyles } from '@COMPONENTS/COMMON/inputs/select/config';
import NoOptionsMessageBase from '@COMPONENTS/COMMON/inputs/select/base/NoOptionsMessageBase';
import ClearIndicatorBase from '@COMPONENTS/COMMON/inputs/select/base/ClearIndicatorBase';
import DropdownIndicatorBase from '@COMPONENTS/COMMON/inputs/select/base/DropdownIndicatorBase';
import DropdownTrash from '@COMPONENTS/COMMON/inputs/select/base/DropdownTrash';
import styles from './BasicSelect.module.scss';

const cx: CX = classnames.bind(styles);

function BasicSelect(props: Props) {
    const {
        title,
        placeholder,
        options,
        value,
        isClearable,
        isSearchable,
        isDisabled,
        isLoading,
        menuPlacement,
        menuPosition,
        menuPortalTarget,
        onChange,
        onMenuOpen,
        onMenuClose,
        onEnterPress,
        isDeleteable,
        onDelete,
        customclassnames,
    } = props;

    const inputId = useId();
    const [inputValue, setInputValue] = React.useState('');

    function handleChange(val: any) {
        onChange(val);
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const inputValueExists = options.some((option) => option.label.includes(inputValue));
        if (event.key === 'Enter' && !inputValueExists && onEnterPress) {
            onEnterPress(inputValue);
            if (event.target instanceof HTMLElement) {
                event.target.blur();
            }
            event.preventDefault();
        }
    };

    const handleDelete = useCallback((option: { value: string; label: string }) => {
        onDelete(option);
    }, [onDelete]);

    const CustomOption = useCallback(
        ({ ...optionProps }: OptionProps<any, true>) => (
            <DropdownTrash {...optionProps} onDelete={handleDelete} />
        ),
        [handleDelete],
    );

    return (
        <div className={cx('basic-select', customclassnames)}>
            <label
                htmlFor={inputId}
                className={cx({
                    'extra-padding': !isDeleteable,
                })}
            >
                <div className={cx('title')}>
                    {title}
                </div>
                {
                    isLoading
                    && (
                        <div className={cx('loading-wrapper')}>
                            <CircularProgressIndicator
                                size={12}
                                thickness={2}
                                color={COLOR['marriott-light-grey']}
                            />
                        </div>
                    )
                }
            </label>
            <Select
                inputId={inputId}
                onInputChange={(newValue) => setInputValue(newValue)}
                onKeyDown={handleKeyDown}
                options={options}
                isClearable={isClearable}
                isSearchable={isSearchable}
                isDisabled={isDisabled}
                placeholder={placeholder}
                menuPlacement={menuPlacement as MenuPlacement}
                menuPosition={menuPosition as MenuPosition}
                menuPortalTarget={menuPortalTarget}
                menuShouldScrollIntoView={false}
                value={value}
                onChange={(val: any) => handleChange(val)}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                unstyled
                components={{
                    IndicatorSeparator: () => null,
                    NoOptionsMessage: NoOptionsMessageBase,
                    ClearIndicator: ClearIndicatorBase,
                    DropdownIndicator: DropdownIndicatorBase,
                    Option: isDeleteable ? CustomOption : components.Option,
                }}
                styles={selectBaseStyles}
                classNames={{
                    container: () => cx('custom-container'),
                    menuList: () => cx('menu-list'),
                    control: (state) => cx('custom-control', {
                        'custom-control--is-focused': state.isFocused,
                        'custom-control--is-disabled': state.isDisabled,
                    }),
                    option: (state) => {
                        if (isDeleteable) {
                            return cx('custom-option-icon', {
                                'custom-option-icon--is-focused': state.isFocused,
                                'custom-option-icon--is-selected': state.isSelected,
                            });
                        }
                        return cx('custom-option', {
                            'custom-option--is-focused': state.isFocused,
                            'custom-option--is-selected': state.isSelected,
                        });
                    },
                    dropdownIndicator: (state) => cx('custom-dropdown-icon', {
                        'is-open': state.selectProps.menuIsOpen,
                        'is-disabled': state.isDisabled,
                    }),
                    menu: () => cx('custom-menu'),
                }}
            />
        </div>
    );
}

BasicSelect.defaultProps = {
    title: '',
    placeholder: 'Select...',
    isClearable: true,
    isSearchable: false,
    isDisabled: false,
    isLoading: false,
    menuPlacement: 'auto',
    menuPosition: 'absolute',
    menuPortalTarget: null,
    onMenuOpen: () => {
    },
    onMenuClose: () => {
    },
    onEnterPress: () => {},
    onDelete: (_data: { value: number; label: string }) => {},
    isDeleteable: false,
    customclassnames: '',
};

BasicSelect.propTypes = {
    title: PropTypes.string,
    placeholder: PropTypes.string,
    isClearable: PropTypes.bool,
    isSearchable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func,
    onMenuClose: PropTypes.func,
    menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
    menuPosition: PropTypes.oneOf(['absolute', 'fixed']),
    menuPortalTarget: PropTypes.oneOfType([
        PropTypes.instanceOf(Element),
        PropTypes.oneOf([null])
    ]),
    onEnterPress: PropTypes.func,
    onDelete: PropTypes.func,
    isDeleteable: PropTypes.bool,
    customclassnames: PropTypes.string,
};

type Props = InferProps<typeof BasicSelect.propTypes> & typeof BasicSelect.defaultProps & {
    options: Options<any>,
    value: SingleValue<any>
    onEnterPress?: (inputValue: string) => void,
    menuPortalTarget?: HTMLElement | null | undefined,
};

export default BasicSelect;
